/**
 * Roof faces that meet along an edge.
 *
 * A second face drawn against a first one shares an edge with it: the ridge
 * of a gable, the hip of a hipped roof, the joint of an annex. Along that
 * edge the two surfaces are at the same height, so the new face's height
 * follows from its neighbour's instead of being typed: the face takes the
 * neighbour's pitch, falls away from the shared edge when that edge is level
 * (a ridge), and gets the height that puts its surface on the neighbour's
 * along the edge. The first face drawn is the reference; each later face is
 * fitted to whatever it touches, and its height stays editable for faces
 * that do not meet level (an annex a storey lower, say).
 *
 * Every surface is described in one plan frame (x east, y north, metres) as
 * z(p) = zAtOrigin + roofZ({ pitchDeg, azimuthDeg }, p - origin), with z
 * relative to the scene's datum - the convention of utils/siteGeometry.
 */
import { roofZ } from "./siteGeometry";
import { edgeOutwardAzimuth, longestEdgeAzimuth } from "./roofAzimuth";

/** Edges this close (metres) count as the same edge; corners usually snap exactly. */
export const EDGE_TOLERANCE_M = 0.3;
/** Two faces touching at less than this are a corner contact, not a shared edge. */
const MIN_OVERLAP_M = 0.5;
/** A shared edge whose ends are within this height of each other is level: a ridge. */
const LEVEL_M = 0.05;

const finite = (v) => v !== "" && v != null && Number.isFinite(Number(v));

/** Height of a face's surface at a plan point. */
export function surfaceZ(face, p) {
  return Number(face.zAtOrigin) + roofZ({ pitchDeg: face.pitchDeg, azimuthDeg: face.azimuthDeg },
    p.x - Number(face.origin?.x || 0), p.y - Number(face.origin?.y || 0));
}

/**
 * Every stretch where an edge of `ring` lies along an edge of `other`:
 * { index (edge of ring, from vertex index to index+1), a, b (the ends of the
 * stretch), mid, length }. Partial overlaps count: a ridge shared
 * by a short face and a long one is still shared.
 */
export function sharedEdges(ring, other, tolerance = EDGE_TOLERANCE_M) {
  if (!Array.isArray(ring) || !Array.isArray(other) || ring.length < 2 || other.length < 2) return [];
  const matches = [];
  for (let i = 0; i < ring.length; i += 1) {
    const p0 = ring[i], p1 = ring[(i + 1) % ring.length];
    const plen = Math.hypot(p1.x - p0.x, p1.y - p0.y);
    if (!(plen > 1e-6)) continue;
    for (let j = 0; j < other.length; j += 1) {
      const q0 = other[j], q1 = other[(j + 1) % other.length];
      const qx = q1.x - q0.x, qy = q1.y - q0.y, qlen = Math.hypot(qx, qy);
      if (!(qlen > 1e-6)) continue;
      const ux = qx / qlen, uy = qy / qlen;
      // Both ends of the ring's edge on the other edge's line, within tolerance.
      const perp = (p) => Math.abs((p.x - q0.x) * uy - (p.y - q0.y) * ux);
      if (perp(p0) > tolerance || perp(p1) > tolerance) continue;
      const s0 = (p0.x - q0.x) * ux + (p0.y - q0.y) * uy, s1 = (p1.x - q0.x) * ux + (p1.y - q0.y) * uy;
      const lo = Math.max(0, Math.min(s0, s1)), hi = Math.min(qlen, Math.max(s0, s1));
      const length = hi - lo;
      if (length < Math.max(MIN_OVERLAP_M, 0.3 * Math.min(plen, qlen))) continue;
      // The stretch runs the way the ring's own edge does.
      const [from, to] = s0 <= s1 ? [lo, hi] : [hi, lo];
      const a = { x: q0.x + ux * from, y: q0.y + uy * from }, b = { x: q0.x + ux * to, y: q0.y + uy * to };
      matches.push({ index: i, a, b, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, length });
    }
  }
  return matches;
}

export function sharedEdge(ring, other, tolerance = EDGE_TOLERANCE_M) {
  return sharedEdges(ring, other, tolerance).reduce((best, edge) =>
    !best || edge.length > best.length ? edge : best, null);
}

/**
 * Fit a new face, outlined by `ring` about `origin`, to the neighbouring
 * surfaces it touches. `wanted` keeps the user's own pitch or azimuth when
 * given; otherwise the pitch is the neighbour's and the azimuth falls away
 * from a level shared edge, or points off the longest edge for a sloping
 * one (a hip), nearest the neighbour's own azimuth.
 *
 * Returns null when no edge is shared, else { neighbour, edge, level,
 * pitchDeg, azimuthDeg, zAtOrigin } - the height that puts this face's
 * surface on the neighbour's at the middle of the shared edge.
 */
export function fitFaceToNeighbours(ring, origin, neighbours, wanted = {}) {
  let best = null;
  (neighbours || []).forEach((neighbour) => {
    if (!neighbour || !finite(neighbour.pitchDeg) || !finite(neighbour.azimuthDeg) || !finite(neighbour.zAtOrigin)) return;
    const edge = sharedEdge(ring, neighbour.ring);
    if (edge && (!best || edge.length > best.edge.length)) best = { neighbour, edge };
  });
  if (!best) return null;
  const { neighbour, edge } = best;
  const level = Math.abs(surfaceZ(neighbour, edge.a) - surfaceZ(neighbour, edge.b)) <= LEVEL_M;
  const pitchDeg = finite(wanted.pitchDeg) ? Number(wanted.pitchDeg) : Number(neighbour.pitchDeg);
  let azimuthDeg;
  if (finite(wanted.azimuthDeg)) azimuthDeg = Number(wanted.azimuthDeg);
  else if (level) {
    // Down and away from the ridge: the opposite of the edge's outward normal.
    const outward = edgeOutwardAzimuth(ring, edge.index);
    azimuthDeg = outward == null ? Number(neighbour.azimuthDeg) : (outward + 180) % 360;
  } else azimuthDeg = longestEdgeAzimuth(ring, Number(neighbour.azimuthDeg)) ?? Number(neighbour.azimuthDeg);
  if (!finite(wanted.azimuthDeg)) {
    // Free to turn: lie on the plane that holds the WHOLE shared edge, so a
    // hip closes at both ends instead of only in the middle.
    const za = { ...edge.a, z: surfaceZ(neighbour, edge.a) }, zb = { ...edge.b, z: surfaceZ(neighbour, edge.b) };
    const plane = planeThroughEdge(za, zb, pitchDeg, azimuthDeg);
    return { neighbour, edge, level, ...plane, zAtOrigin: zAtOriginThrough(plane, origin, za) };
  }
  const zEdge = surfaceZ(neighbour, edge.mid);
  const zAtOrigin = zEdge - roofZ({ pitchDeg, azimuthDeg }, edge.mid.x - origin.x, edge.mid.y - origin.y);
  return { neighbour, edge, level, pitchDeg, azimuthDeg, zAtOrigin };
}

const norm360 = (a) => ((Number(a) % 360) + 360) % 360;
const angleGap = (a, b) => { const d = Math.abs(norm360(a) - norm360(b)) % 360; return Math.min(d, 360 - d); };

/**
 * The plane of a face that contains a given 3D edge, at the face's own pitch.
 *
 * Two roof faces meet along their shared edge only if each plane contains the
 * whole edge, not just its middle. Matching the height at one point leaves a
 * hip twisted open at both ends. With the edge's two end heights fixed by the
 * neighbour, a plane of a given pitch has at most two facings that contain it;
 * the one nearer `preferAzimuth` is taken. A pitch too shallow for the edge's
 * own slope is raised to the edge's slope (the face then falls along it).
 *
 * @returns { pitchDeg, azimuthDeg }
 */
export function planeThroughEdge(a, b, pitchDeg, preferAzimuth, inwardAzimuth = null) {
  const ex = b.x - a.x, ey = b.y - a.y;
  const L = Math.hypot(ex, ey);
  const dz = Number(b.z) - Number(a.z);
  let pitch = Math.max(0, Number(pitchDeg) || 0);
  if (!(L > 1e-6)) return { pitchDeg: pitch, azimuthDeg: norm360(preferAzimuth ?? 180) };
  const edgeSlope = Math.atan(Math.abs(dz) / L) * 180 / Math.PI;
  if (pitch < edgeSlope) pitch = edgeSlope;
  const tanP = Math.tan(pitch * Math.PI / 180);
  if (!(tanP > 1e-9)) return { pitchDeg: 0, azimuthDeg: norm360(preferAzimuth ?? 180) };
  // z = -tan(p) (x sin az + y cos az): along the edge, -tan(p) L cos(az - phi) = dz.
  const phi = Math.atan2(ex, ey) * 180 / Math.PI;
  const c = Math.max(-1, Math.min(1, -dz / (L * tanP)));
  const spread = Math.acos(c) * 180 / Math.PI;
  const options = [norm360(phi + spread), norm360(phi - spread)];
  const want = preferAzimuth == null ? (inwardAzimuth ?? options[0]) : Number(preferAzimuth);
  let gap0 = angleGap(options[0], want), gap1 = angleGap(options[1], want);
  // A tie (the preferred facing exactly between the two) goes to the face
  // falling away from the edge, into its own outline: a ridge or a hip, the
  // usual case, rather than a valley.
  if (Math.abs(gap0 - gap1) < 1 && inwardAzimuth != null) {
    gap0 = angleGap(options[0], inwardAzimuth); gap1 = angleGap(options[1], inwardAzimuth);
  }
  const azimuthDeg = gap0 <= gap1 ? options[0] : options[1];
  return { pitchDeg: Math.round(pitch * 1e9) / 1e9, azimuthDeg: Math.round(azimuthDeg * 1e9) / 1e9 % 360 };
}

/** zAtOrigin of a plane (pitch, azimuth) about `origin` passing through point p (with z). */
export const zAtOriginThrough = (plane, origin, p) =>
  Math.round((Number(p.z) - roofZ(plane, p.x - Number(origin?.x || 0), p.y - Number(origin?.y || 0))) * 1e9) / 1e9;

/**
 * Make the faces of a roof meet: one connected roof, moved together.
 *
 * Starting from the face being edited (`anchorKey`, which stays where it is),
 * every face reached across a shared edge is fitted to the face it was reached
 * from. A face whose angles are free (a roof face without PV) is turned onto
 * the plane that contains the whole shared edge; a face whose angles are fixed
 * (a PV face: its pitch and facing ARE its orientation) can only be raised or
 * lowered, so it is matched at the middle of the edge and any step left at the
 * ends is reported, with the facing that would close it.
 *
 * Faces marked `independent` (an annex a storey lower, say) are neither moved
 * nor crossed.
 *
 * @param faces [{ key, ring, origin, pitchDeg, azimuthDeg, zAtOrigin, fixedAngles, independent }]
 * @returns { updates: { [key]: { zAtOrigin, pitchDeg, azimuthDeg } },
 *            mismatches: [{ from, to, stepM, anchorAzimuthDeg }] }
 */
export function joinRoofFaces(faces, anchorKey, { stepTolerance = 0.05 } = {}) {
  const list = (faces || []).filter((f) => f && Array.isArray(f.ring) && f.ring.length >= 3 &&
    [f.pitchDeg, f.azimuthDeg, f.zAtOrigin].every(finite));
  const anchor = list.find((f) => f.key === anchorKey);
  const updates = {}, mismatches = [];
  if (!anchor) return { updates, mismatches };
  const resolved = new Map([[anchor.key, { ...anchor }]]);
  const queue = [anchor.key];
  while (queue.length) {
    const current = resolved.get(queue.shift());
    list.forEach((face) => {
      if (resolved.has(face.key) || face.independent) return;
      const edge = sharedEdge(face.ring, current.ring);
      if (!edge) return;
      const za = { ...edge.a, z: surfaceZ(current, edge.a) };
      const zb = { ...edge.b, z: surfaceZ(current, edge.b) };
      let next;
      if (!face.fixedAngles) {
        const c = face.ring.reduce((acc, p) => ({ x: acc.x + p.x / face.ring.length, y: acc.y + p.y / face.ring.length }), { x: 0, y: 0 });
        const inward = norm360(Math.atan2(c.x - edge.mid.x, c.y - edge.mid.y) * 180 / Math.PI);
        const plane = planeThroughEdge(za, zb, face.pitchDeg, face.azimuthDeg, inward);
        next = { ...face, ...plane, zAtOrigin: zAtOriginThrough(plane, face.origin, za) };
      } else {
        const mid = { ...edge.mid, z: surfaceZ(current, edge.mid) };
        next = { ...face, zAtOrigin: zAtOriginThrough(face, face.origin, mid) };
        const step = Math.max(Math.abs(surfaceZ(next, edge.a) - za.z), Math.abs(surfaceZ(next, edge.b) - zb.z));
        if (step > stepTolerance) {
          // What the face being edited would need to face for the two to meet
          // along the whole edge, keeping its own pitch.
          const fa = { ...edge.a, z: surfaceZ(next, edge.a) }, fb = { ...edge.b, z: surfaceZ(next, edge.b) };
          const fit = current.key === anchor.key ? planeThroughEdge(fa, fb, anchor.pitchDeg, anchor.azimuthDeg) : null;
          // ... and what the face being fitted would need, keeping its pitch.
          const own = planeThroughEdge(za, zb, face.pitchDeg, face.azimuthDeg);
          mismatches.push({ from: current.key, to: face.key, stepM: step,
            anchorAzimuthDeg: fit && Math.abs(fit.pitchDeg - Number(anchor.pitchDeg)) < 0.05 ? fit.azimuthDeg : null,
            toAzimuthDeg: Math.abs(own.pitchDeg - Number(face.pitchDeg)) < 0.05 ? own.azimuthDeg : null });
        }
      }
      resolved.set(face.key, next);
      queue.push(face.key);
      const moved = Math.abs(next.zAtOrigin - face.zAtOrigin) > 0.001 ||
        angleGap(next.azimuthDeg, face.azimuthDeg) > 0.01 || Math.abs(next.pitchDeg - face.pitchDeg) > 0.01;
      if (moved) updates[face.key] = { zAtOrigin: next.zAtOrigin, pitchDeg: next.pitchDeg, azimuthDeg: next.azimuthDeg };
    });
  }
  return { updates, mismatches };
}

// ---------------------------------------------------------------------------
// The roof as drawn: each face is set by its eave.
//
// A face is a plane through the midpoint of its reference eave, at the eave
// height. By default its facing is the eave's outward normal. A user-selected
// free azimuth keeps the same height reference and may put the two ends of
// that edge at different elevations. Heights are above the face's own ground.
// ---------------------------------------------------------------------------

const edgeLength = (ring, i) => {
  const a = ring[i], b = ring[(i + 1) % ring.length];
  return Math.hypot(b.x - a.x, b.y - a.y);
};

/** The edge lowest under a plane: the eave a face already has. Flat: the longest edge. */
export function lowestEdge(ring, plane) {
  if (!Array.isArray(ring) || ring.length < 3) return 0;
  const flat = !plane || !(Number(plane.pitchDeg) > 0.5);
  let best = 0, bestScore = Infinity;
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length];
    const length = edgeLength(ring, i);
    if (length < 0.3) return;
    const score = flat ? -length : roofZ(plane, (a.x + b.x) / 2, (a.y + b.y) / 2) - 1e-6 * length;
    if (score < bestScore) { best = i; bestScore = score; }
  });
  return best;
}

/**
 * Resolve one face from its eave.
 *
 * @param ring    the outline, in the frame `origin` is given in
 * @param origin  the point the face's height is referred to
 * @param face    { pitchDeg, eaveEdge?, eaveHeightM?, freeAzimuth?, current?, sharedEdges? }
 *                where current = { azimuthDeg, zAtOrigin } is the plane the
 *                face has now, used for a free azimuth and for the eave height
 *                when none is stored, and sharedEdges the indices it shares with
 *                other faces. With no stored eave, the eave is the longest
 *                edge on the building's outline (not shared): eaves are where
 *                a roof ends, ridges and hips where faces meet.
 * @returns { eaveEdge, eaveHeightM, pitchDeg, azimuthDeg, zAtOrigin }
 */
export function eaveFace(ring, origin, face) {
  const pitchDeg = Math.max(0, Number(face?.pitchDeg) || 0);
  const ox = Number(origin?.x) || 0, oy = Number(origin?.y) || 0;
  const n = Array.isArray(ring) ? ring.length : 0;
  if (n < 3) return null;
  const cur = face?.current && finite(face.current.azimuthDeg)
    ? { pitchDeg, azimuthDeg: Number(face.current.azimuthDeg), zAtOrigin: Number(face.current.zAtOrigin) || 0 } : null;
  const stored = Number(face?.eaveEdge);
  const shared = new Set(face?.sharedEdges || []);
  const outer = ring.map((_, i) => i).filter((i) => !shared.has(i) && edgeLength(ring, i) >= 0.3);
  const longestOuter = outer.length && shared.size
    ? outer.reduce((best, i) => (edgeLength(ring, i) > edgeLength(ring, best) + 1e-6 ? i : best), outer[0]) : null;
  const eaveEdge = Number.isInteger(stored) && stored >= 0 && stored < n && edgeLength(ring, stored) > 1e-6
    ? stored
    : longestOuter ?? lowestEdge(ring.map((p) => ({ x: p.x - ox, y: p.y - oy })), cur);
  const a = ring[eaveEdge], b = ring[(eaveEdge + 1) % n];
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  // A flat face has no downhill: its bearing is kept as it is, so a flat
  // roof is not turned to face an edge it does not slope to.
  const flat = pitchDeg <= 0.05;
  const azimuthDeg = ((flat || face?.freeAzimuth) && cur ? cur.azimuthDeg : edgeOutwardAzimuth(ring, eaveEdge)) ?? cur?.azimuthDeg ?? 180;
  const eaveHeightM = finite(face?.eaveHeightM) ? Number(face.eaveHeightM)
    : cur ? cur.zAtOrigin + roofZ(cur, mid.x - ox, mid.y - oy) : 0;
  const plane = { pitchDeg, azimuthDeg };
  return {
    // Round display labels, not the plane. Coarse angular rounding opens a
    // visible joint on a long roof even when its shared endpoints fitted.
    eaveEdge, pitchDeg, azimuthDeg: Math.round(norm360(azimuthDeg) * 1e9) / 1e9 % 360,
    eaveHeightM: Math.round(eaveHeightM * 1e9) / 1e9,
    zAtOrigin: Math.round((eaveHeightM - roofZ(plane, mid.x - ox, mid.y - oy)) * 1e9) / 1e9,
  };
}

/**
 * Fit a newly drawn face to an existing face without moving either outline.
 * Its downhill direction stays perpendicular to its outer eave. A sloping
 * shared edge determines the new pitch; a level ridge leaves the pitch free,
 * so the preferred pitch is used. Return null when that eave cannot meet the
 * whole shared edge with a valid upward roof pitch.
 */
export function fitDrawnEaveFace(ring, origin, neighbours, preferredPitchDeg = 0) {
  const valid = (neighbours || []).filter((n) => n && Array.isArray(n.ring) && n.ring.length >= 3 &&
    [n.pitchDeg, n.azimuthDeg, n.zAtOrigin].every(finite));
  let best = null;
  valid.forEach((neighbour) => {
    const edge = sharedEdge(ring, neighbour.ring);
    if (edge && (!best || edge.length > best.edge.length)) best = { neighbour, edge };
  });
  if (!best) return null;
  const shared = sharedEdgeIndices([ring, ...valid.map((n) => n.ring)])[0];
  const base = eaveFace(ring, origin, { pitchDeg: preferredPitchDeg, sharedEdges: shared });
  if (!base) return null;
  const a = ring[base.eaveEdge], b = ring[(base.eaveEdge + 1) % ring.length];
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const direction = { x: Math.sin(base.azimuthDeg * Math.PI / 180), y: Math.cos(base.azimuthDeg * Math.PI / 180) };
  const projection = (p) => (p.x - mid.x) * direction.x + (p.y - mid.y) * direction.y;
  const da = projection(best.edge.a), db = projection(best.edge.b);
  const za = surfaceZ(best.neighbour, best.edge.a), zb = surfaceZ(best.neighbour, best.edge.b);
  const delta = db - da, rise = zb - za;
  if (Math.abs(delta) < 0.01 && Math.abs(rise) > LEVEL_M) return null;
  const tangent = Math.abs(delta) < 0.01
    ? Math.tan(Math.max(0, Number(preferredPitchDeg) || 0) * Math.PI / 180)
    : -rise / delta;
  if (!Number.isFinite(tangent) || tangent < -0.001 || tangent > Math.tan(75 * Math.PI / 180)) return null;
  const pitchDeg = Math.atan(Math.max(0, tangent)) * 180 / Math.PI;
  const eaveHeightM = za + tangent * da;
  const fitted = eaveFace(ring, origin, { pitchDeg, eaveEdge: base.eaveEdge, eaveHeightM });
  if (!fitted) return null;
  const face = { ...fitted, ring, origin };
  if (Math.max(Math.abs(surfaceZ(face, best.edge.a) - za), Math.abs(surfaceZ(face, best.edge.b) - zb)) > LEVEL_M) return null;
  return { ...fitted, neighbour: best.neighbour, edge: best.edge };
}

/** Fit a newly drawn roof face against every edge it shares with existing faces.
 * First keep the usual level eave when that closes every joint. A roof with a
 * skewed hip may instead need a free downhill bearing; the plane through all
 * shared edge endpoints supplies it. Incompatible neighbours are reported,
 * never silently joined at only one edge or one midpoint.
 */
export function fitDrawnRoofFace(ring, origin, neighbours, preferredPitchDeg = 0, tolerance = 1e-6) {
  const contacts = (neighbours || []).flatMap((neighbour) => {
    if (!neighbour || !Array.isArray(neighbour.ring) ||
        ![neighbour.pitchDeg, neighbour.azimuthDeg, neighbour.zAtOrigin].every(finite)) return [];
    return sharedEdges(ring, neighbour.ring).map(edge => ({ neighbour, edge }));
  });
  if (!contacts.length) return { status: "unconnected", face: null };
  const matchesAll = (face) => face && contacts.every(({ neighbour, edge }) =>
    [edge.a, edge.b].every((p) => Math.abs(surfaceZ(face, p) - surfaceZ(neighbour, p)) <= tolerance));
  const withFrame = (face) => ({ ...face, ring, origin });

  const eave = fitDrawnEaveFace(ring, origin, neighbours, preferredPitchDeg);
  if (matchesAll(eave && withFrame(eave))) return { status: "fitted", face: eave };

  const samples = contacts.flatMap(({ neighbour, edge }) => [edge.a, edge.b].map((p) =>
    ({ x: p.x, y: p.y, z: surfaceZ(neighbour, p) })));
  const count = samples.length;
  const mean = samples.reduce((acc, p) => ({ x: acc.x + p.x / count,
    y: acc.y + p.y / count, z: acc.z + p.z / count }), { x: 0, y: 0, z: 0 });
  let xx = 0, xy = 0, yy = 0, xz = 0, yz = 0;
  samples.forEach((p) => {
    const x = p.x - mean.x, y = p.y - mean.y, z = p.z - mean.z;
    xx += x * x; xy += x * y; yy += y * y; xz += x * z; yz += y * z;
  });
  const det = xx * yy - xy * xy;
  let plane = null;
  if (det > 1e-10 * (xx + yy) ** 2) {
    const gx = (xz * yy - yz * xy) / det, gy = (yz * xx - xz * xy) / det;
    const pitchDeg = Math.atan(Math.hypot(gx, gy)) * 180 / Math.PI;
    if (pitchDeg <= 75) plane = { pitchDeg,
      azimuthDeg: Math.hypot(gx, gy) < 1e-9 ? norm360(contacts[0].neighbour.azimuthDeg)
        : norm360(Math.atan2(-gx, -gy) * 180 / Math.PI),
      zAtOrigin: mean.z + gx * (origin.x - mean.x) + gy * (origin.y - mean.y) };
  } else {
    // One straight shared edge leaves a family of valid planes. Preserve the
    // neighbour's pitch and choose the one that contains that entire edge.
    const fitted = fitFaceToNeighbours(ring, origin, neighbours);
    if (fitted) plane = fitted;
  }
  if (!matchesAll(plane && withFrame(plane))) return { status: "conflict", face: null };

  const shared = sharedEdgeIndices([ring, ...(neighbours || []).map((n) => n?.ring)])[0];
  const saved = eaveFace(ring, origin, { pitchDeg: plane.pitchDeg, freeAzimuth: true,
    sharedEdges: shared, current: { azimuthDeg: plane.azimuthDeg, zAtOrigin: plane.zAtOrigin } });
  if (!matchesAll(saved && withFrame(saved))) return { status: "conflict", face: null };
  return { status: "fitted", face: { ...saved, freeAzimuth: true } };
}

/**
 * The edge whose outward normal is nearest a bearing: the eave a face facing
 * that way has, and the one a dragged slope arrow points out of.
 */
export function facingEdge(ring, azimuthDeg) {
  if (!Array.isArray(ring) || ring.length < 3 || !finite(azimuthDeg)) return null;
  let best = null, bestGap = Infinity;
  ring.forEach((_, i) => {
    if (edgeLength(ring, i) < 1e-6) return;
    const out = edgeOutwardAzimuth(ring, i);
    if (out == null) return;
    const gap = angleGap(out, azimuthDeg);
    if (gap < bestGap - 1e-9) { best = i; bestGap = gap; }
  });
  return best;
}

/** For each face (rings in one frame), the indices of its edges shared with another. */
export function sharedEdgeIndices(rings) {
  return rings.map((ring, i) => {
    const out = [];
    if (!Array.isArray(ring)) return out;
    ring.forEach((a, k) => {
      const b = ring[(k + 1) % ring.length];
      const seg = [a, b];
      if (rings.some((other, j) => j !== i && Array.isArray(other) && sharedEdge(seg, other)?.length > 0.3)) out.push(k);
    });
    return out;
  });
}

/**
 * Where two faces that share an edge do not meet: the larger gap at the
 * edge's ends, for every pair over `tolerance` metres. Faces carry absolute
 * surfaces ({ key, ring, origin, pitchDeg, azimuthDeg, zAtOrigin } in one frame).
 */
export function edgeSteps(faces, tolerance = 1e-6) {
  const list = (faces || []).filter((f) => f && Array.isArray(f.ring) && f.ring.length >= 3);
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const edges = sharedEdges(list[i].ring, list[j].ring);
      if (!edges.length) continue;
      const step = Math.max(...edges.flatMap(edge => [
        Math.abs(surfaceZ(list[i], edge.a) - surfaceZ(list[j], edge.a)),
        Math.abs(surfaceZ(list[i], edge.b) - surfaceZ(list[j], edge.b))]));
      if (step > tolerance) out.push({ a: list[i].key, b: list[j].key, stepM: step });
    }
  }
  return out;
}

// Project the existing planes onto the exact shared-edge constraints. Solving
// the whole connected roof at once closes cycles and both ends of every edge;
// fitting neighbours successively can reopen an edge already visited.
export function fitRoofJoints(faces, anchorKey, tolerance = 1e-6) {
  const list = (faces || []).filter(f => f?.ring?.length >= 3 &&
    [f.pitchDeg, f.azimuthDeg, f.zAtOrigin].every(finite));
  const contacts = [];
  for (let i = 0; i < list.length; i++) for (let j = i + 1; j < list.length; j++) {
    const edges = sharedEdges(list[i].ring, list[j].ring);
    if (edges.length) contacts.push({ i, j, edges });
  }
  const updates = {};
  const visited = new Set();
  const dot = (a, b) => a.reduce((sum, v, i) => sum + v * b[i], 0);
  const solve = (initial, constraints) => {
    const basis = [];
    for (const constraint of constraints) {
      const row = [...constraint.row]; let target = constraint.target;
      // Reorthogonalise: roof loops contain many redundant constraints.
      for (let pass = 0; pass < 2; pass++) for (const q of basis) {
        const factor = dot(row, q.row);
        row.forEach((v, k) => { row[k] = v - factor * q.row[k]; });
        target -= factor * q.target;
      }
      const length = Math.sqrt(dot(row, row));
      if (length < 1e-10) { if (Math.abs(target) > tolerance / 10) return null; continue; }
      basis.push({ row: row.map(v => v / length), target: target / length });
    }
    const result = [...initial];
    for (const q of basis) {
      const delta = q.target - dot(initial, q.row);
      result.forEach((v, k) => { result[k] = v + delta * q.row[k]; });
    }
    return constraints.every(c => Math.abs(dot(result, c.row) - c.target) < tolerance / 10) ? result : null;
  };
  list.forEach((_, start) => {
    if (visited.has(start)) return;
    const component = [start]; visited.add(start);
    for (let p = 0; p < component.length; p++) for (const { i, j } of contacts) {
      const next = i === component[p] ? j : j === component[p] ? i : null;
      if (next != null && !visited.has(next)) { visited.add(next); component.push(next); }
    }
    const group = component.map(i => list[i]);
    if (!edgeSteps(group, tolerance).length) return;
    const slots = new Map(component.map((i, k) => [i, k]));
    const scale = Math.max(1, ...group.flatMap(f => f.ring.map(p =>
      Math.hypot(p.x - (f.origin?.x || 0), p.y - (f.origin?.y || 0)))));
    const initial = group.flatMap(f => {
      const t = Math.tan(f.pitchDeg * Math.PI / 180), a = f.azimuthDeg * Math.PI / 180;
      return [Number(f.zAtOrigin), -t * Math.sin(a) * scale, -t * Math.cos(a) * scale];
    });
    const at = (slot, point) => {
      const row = Array(initial.length).fill(0), f = group[slot];
      row[slot * 3] = 1;
      row[slot * 3 + 1] = (point.x - (f.origin?.x || 0)) / scale;
      row[slot * 3 + 2] = (point.y - (f.origin?.y || 0)) / scale;
      return row;
    };
    const constraints = contacts.filter(c => slots.has(c.i) && slots.has(c.j)).flatMap(({ i, j, edges }) =>
      edges.flatMap(edge => [edge.a, edge.b].map(point => {
        const a = at(slots.get(i), point), b = at(slots.get(j), point);
        return { row: a.map((v, k) => v - b[k]), target: 0 };
      })));
    const anchor = Math.max(0, group.findIndex(f => f.key === anchorKey));
    for (let k = 0; k < 3; k++) {
      const row = Array(initial.length).fill(0); row[anchor * 3 + k] = 1;
      constraints.push({ row, target: initial[anchor * 3 + k] });
    }
    const eaves = group.flatMap((f, slot) => {
      if (!Number.isInteger(f.eaveEdge)) return [];
      const a = f.ring[f.eaveEdge], b = f.ring[(f.eaveEdge + 1) % f.ring.length];
      if (!a || !b) return [];
      const point = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      return [{ row: at(slot, point), target: surfaceZ(f, point) }];
    });
    // Keep all requested eave heights when compatible. Shared edges take
    // priority when the drawn outline makes those heights incompatible.
    const fitted = solve(initial, [...constraints, ...eaves]) || solve(initial, constraints);
    if (!fitted) return;
    const proposed = group.map((f, slot) => {
      const gx = fitted[slot * 3 + 1] / scale, gy = fitted[slot * 3 + 2] / scale;
      return { ...f, zAtOrigin: fitted[slot * 3], pitchDeg: Math.atan(Math.hypot(gx, gy)) * 180 / Math.PI,
        azimuthDeg: Math.hypot(gx, gy) < 1e-10 ? f.azimuthDeg : norm360(Math.atan2(-gx, -gy) * 180 / Math.PI) };
    });
    if (proposed.some(f => f.pitchDeg >= 75) || edgeSteps(proposed, tolerance).length) return;
    proposed.forEach((f, i) => {
      if (Math.abs(f.zAtOrigin - group[i].zAtOrigin) > 1e-8 || Math.abs(f.pitchDeg - group[i].pitchDeg) > 1e-8 ||
          angleGap(f.azimuthDeg, group[i].azimuthDeg) > 1e-8) {
        updates[f.key] = { zAtOrigin: f.zAtOrigin, pitchDeg: f.pitchDeg, azimuthDeg: f.azimuthDeg };
      }
    });
  });
  const resolved = list.map(f => ({ ...f, ...updates[f.key] }));
  return { updates, mismatches: edgeSteps(resolved, tolerance) };
}

/**
 * Close the roof where faces meet inside it: the ridge and hip points.
 *
 * Faces with one eave height and one pitch meet along the bisectors of their
 * eave corners, and nowhere else; a hip or ridge drawn by hand is never quite
 * there, so the two surfaces part along it and a step, growing towards the
 * apex, shows in the scene. Each point that two or more faces share and that
 * is on none of their eaves is moved to where their surfaces meet: on the
 * line where two planes cross, the nearest point; where three or more meet,
 * the point closest to all of them. Eaves never move, so no face's plane
 * changes, only the inner corners of the outlines. A point that would have
 * to move further than `maxMove` metres (or than 40% of its shortest edge)
 * stays: the faces do not belong to one roof as they stand (eave heights
 * apart, say), and the step is reported instead.
 *
 * @param faces [{ key, ring, origin, pitchDeg, azimuthDeg, zAtOrigin,
 *              eaveEdge, fixed }] in one plan frame and one datum; `fixed`
 *              faces (rectangles given by width and bearing) keep their corners
 * @returns { [key]: ring } for the faces whose outline moved
 */
export function closeRoofVertices(faces, { tolerance = 0.05, maxMove = 1.5, minMove = 0.002 } = {}) {
  const list = (faces || []).filter((f) => f && Array.isArray(f.ring) && f.ring.length >= 3
    && f.ring.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y)));
  const refs = [];
  list.forEach((f, fi) => f.ring.forEach((p, vi) => refs.push({ fi, vi, p })));
  const used = new Set();
  const groups = [];
  refs.forEach((r, i) => {
    if (used.has(i)) return;
    const group = [r];
    used.add(i);
    refs.forEach((s, j) => {
      if (!used.has(j) && s.fi !== r.fi && Math.hypot(s.p.x - r.p.x, s.p.y - r.p.y) <= tolerance) { group.push(s); used.add(j); }
    });
    if (new Set(group.map((x) => x.fi)).size >= 2) groups.push(group);
  });
  const onEave = (f, vi) => Number.isInteger(f.eaveEdge) && (vi === f.eaveEdge || vi === (f.eaveEdge + 1) % f.ring.length);
  // z(p) = g · p + c for each face's plane.
  const plane = (f) => {
    const t = Math.tan((Number(f.pitchDeg) || 0) * Math.PI / 180);
    const az = (Number(f.azimuthDeg) || 0) * Math.PI / 180;
    const dx = Math.sin(az), dy = Math.cos(az);
    const ox = Number(f.origin?.x) || 0, oy = Number(f.origin?.y) || 0;
    return { gx: -t * dx, gy: -t * dy, c: Number(f.zAtOrigin) + t * (ox * dx + oy * dy) };
  };
  const planes = list.map(plane);
  const next = list.map((f) => f.ring.map((p) => ({ x: p.x, y: p.y })));
  const moved = new Set();
  groups.forEach((group) => {
    if (group.some(({ fi, vi }) => list[fi].fixed || onEave(list[fi], vi))) return;
    const fis = [...new Set(group.map((x) => x.fi))];
    const p0 = { x: group.reduce((s, x) => s + x.p.x, 0) / group.length, y: group.reduce((s, x) => s + x.p.y, 0) / group.length };
    // Least squares on the height differences, with a faint pull to where the
    // point is: on a line (two faces) that picks its nearest point.
    const lambda = 1e-6;
    let a11 = lambda, a12 = 0, a22 = lambda, b1 = lambda * p0.x, b2 = lambda * p0.y;
    for (let i = 0; i < fis.length; i += 1) for (let j = i + 1; j < fis.length; j += 1) {
      const pi = planes[fis[i]], pj = planes[fis[j]];
      const gx = pi.gx - pj.gx, gy = pi.gy - pj.gy, c = pi.c - pj.c;
      a11 += gx * gx; a12 += gx * gy; a22 += gy * gy; b1 -= gx * c; b2 -= gy * c;
    }
    const det = a11 * a22 - a12 * a12;
    if (!(Math.abs(det) > 1e-18)) return;
    const p = { x: (a22 * b1 - a12 * b2) / det, y: (a11 * b2 - a12 * b1) / det };
    if (![p.x, p.y].every(Number.isFinite)) return;
    const shortest = Math.min(...group.flatMap(({ fi, vi }) => {
      const ring = list[fi].ring, n = ring.length;
      return [ring[(vi + n - 1) % n], ring[(vi + 1) % n]].map((q) => Math.hypot(q.x - ring[vi].x, q.y - ring[vi].y));
    }));
    if (Math.hypot(p.x - p0.x, p.y - p0.y) > Math.min(maxMove, 0.4 * shortest)) return;
    if (group.every(({ p: q }) => Math.hypot(q.x - p.x, q.y - p.y) < minMove)) return;
    group.forEach(({ fi, vi }) => { next[fi][vi] = { x: p.x, y: p.y }; moved.add(fi); });
  });
  return Object.fromEntries([...moved].map((fi) => [list[fi].key, next[fi]]));
}
